import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import { Field, Formik } from "formik";
import { CheckboxWithLabel } from "formik-material-ui";
import { Alert as MuiAlert } from "@material-ui/lab";
import {
  Avatar,
  Box,
  Button,
  Grid,
  InputLabel,
  Paper,
  TextField as MuiTextField,
  Typography,
  FormControl as MuiFormControl,
} from "@material-ui/core";
import { spacing } from "@material-ui/system";
import { completeGoogleSignUp } from "../../redux/actions/authActions";
import AuthLayout from "../../layouts/Auth";
import SelectWithAPI from "../../components/SelectWithAPI";
import {
  clearPendingSocialRegistration,
  loadPendingSocialRegistration,
} from "../../utils/socialAuth";
import { clearReferralAttribution } from "../../utils/referralAttribution";

const TextField = styled(MuiTextField)(spacing);
const Alert = styled(MuiAlert)(spacing);

const Wrapper = styled(Paper)`
  width: 100%;
  padding: ${(props) => props.theme.spacing(4)}px;
  border-radius: 30px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 28px 64px rgba(15, 23, 42, 0.12);
`;

const BigAvatar = styled(Avatar)`
  width: 84px;
  height: 84px;
  text-align: center;
  margin: 0;
  box-shadow: 0 18px 34px rgba(37, 99, 235, 0.18);
`;

const Hero = styled(Box)`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 24px;
  padding: 24px;
  border-radius: 24px;
  background: linear-gradient(135deg, #f8fbff 0%, #eef4ff 45%, #f8fafc 100%);
  border: 1px solid rgba(37, 99, 235, 0.1);
  margin-bottom: 24px;

  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: 110px minmax(0, 1fr);
    align-items: center;
  }
`;

const HeroCopy = styled(Box)`
  min-width: 0;
`;

const Eyebrow = styled(Box)`
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.1);
  color: #2563eb;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const HeroTitle = styled(Typography)`
  margin-top: 14px;
  font-size: clamp(2rem, 3vw, 2.6rem);
  font-weight: 800;
  line-height: 1.06;
  color: #0f172a;
`;

const HeroDescription = styled(Typography)`
  margin-top: 12px;
  color: #64748b;
  line-height: 1.75;
`;

const SectionCard = styled(Box)`
  padding: 22px;
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: #ffffff;
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.06);
`;

const SectionHeader = styled(Box)`
  margin-bottom: 18px;
`;

const SectionEyebrow = styled(Typography)`
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #2563eb;
  font-weight: 700;
`;

const SectionTitle = styled(Typography)`
  margin-top: 6px;
  font-size: 1.3rem;
  font-weight: 800;
  color: #0f172a;
`;

const SectionDescription = styled(Typography)`
  margin-top: 6px;
  color: #64748b;
  line-height: 1.6;
`;

const StyledFormControl = styled(MuiFormControl)`
  width: 100%;
`;

const CheckboxRow = styled(Box)`
  margin-top: 8px;
  padding: 14px 16px;
  border-radius: 18px;
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.16);
`;

const SubmitButton = styled(Button)`
  min-height: 52px;
  margin-top: 18px;
  border-radius: 18px;
  text-transform: none;
  font-size: 1rem;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
`;

function SocialCompleteRegistration() {
  const dispatch = useDispatch();
  const history = useHistory();
  const pendingSocialRegistration = loadPendingSocialRegistration();
  const phoneRegexp = /^(?:\+\d{1,5}\s?)?(?:\(\d{1,5}\)|\d{1,5})[- .]?\d{1,5}[- .]?\d{1,5}$/;

  useEffect(() => {
    if (!pendingSocialRegistration?.credential) {
      history.replace("/auth/sign-in");
    }
  }, [history, pendingSocialRegistration]);

  if (!pendingSocialRegistration?.credential) {
    return null;
  }

  return (
    <AuthLayout>
      <Wrapper>
        <Helmet title="Complete Social Registration" />
        <Hero>
          <BigAvatar alt="Wifiads" src="/static/img/avatars/logo.png" />
          <HeroCopy>
            <Eyebrow>Google Signup</Eyebrow>
            <HeroTitle>Complete su registro</HeroTitle>
            <HeroDescription>
              Validamos su identidad con Google. Ahora complete los datos
              comerciales y acepte los términos para activar su cuenta.
            </HeroDescription>
          </HeroCopy>
        </Hero>

        <Formik
          initialValues={{
            firstName: pendingSocialRegistration.profile?.firstName || "",
            lastName: pendingSocialRegistration.profile?.lastName || "",
            email: pendingSocialRegistration.profile?.email || "",
            company: "",
            businessType: "",
            phone: "",
            mobileNumber: "",
            identificationType: "",
            identificationNumber: "",
            streetAddress: "",
            city: "",
            country: "",
            referral: pendingSocialRegistration.referral || "",
            eula: false,
            submit: false,
          }}
          validationSchema={Yup.object().shape({
            firstName: Yup.string().max(255).required("Campo requerido."),
            lastName: Yup.string().max(255).required("Campo requerido."),
            company: Yup.string().required("Campo requerido"),
            businessType: Yup.string().required("Campo requerido"),
            phone: Yup.string()
              .matches(phoneRegexp, "Teléfono inválido")
              .required("Campo requerido"),
            mobileNumber: Yup.string()
              .matches(phoneRegexp, "Teléfono inválido")
              .required("Campo requerido"),
            identificationType: Yup.string().required("Campo requerido"),
            identificationNumber: Yup.string().required("Campo requerido"),
            streetAddress: Yup.string().required("Campo requerido"),
            city: Yup.string().required("Campo requerido"),
            country: Yup.string().required("Campo requerido"),
            eula: Yup.boolean().oneOf([true], "Debe aceptar los términos."),
          })}
          onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
            try {
              await dispatch(
                completeGoogleSignUp({
                  credential: pendingSocialRegistration.credential,
                  firstName: values.firstName,
                  lastName: values.lastName,
                  company: values.company,
                  businessType: values.businessType,
                  phone: values.phone,
                  mobileNumber: values.mobileNumber,
                  identificationType: values.identificationType,
                  identificationNumber: values.identificationNumber,
                  streetAddress: values.streetAddress,
                  city: values.city,
                  country: values.country,
                  eulaAgreed: values.eula,
                  referral: values.referral,
                })
              );

              clearPendingSocialRegistration();
              clearReferralAttribution();
              history.push("/dashboard");
            } catch (error) {
              const backendMessage =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "No fue posible completar el registro social.";

              setStatus({ success: false });
              setErrors({ submit: backendMessage });
              setSubmitting(false);
            }
          }}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            touched,
            values,
          }) => (
            <form noValidate onSubmit={handleSubmit}>
              {errors.submit && (
                <Alert mt={2} mb={2} severity="warning">
                  {errors.submit}
                </Alert>
              )}

              <SectionCard>
                <SectionHeader>
                  <SectionEyebrow>Datos de acceso</SectionEyebrow>
                  <SectionTitle>Cuenta validada con Google</SectionTitle>
                  <SectionDescription>
                    El correo llega desde Google y se usará como identidad de su
                    cuenta en la plataforma.
                  </SectionDescription>
                </SectionHeader>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="firstName"
                      label="Nombre"
                      value={values.firstName}
                      error={Boolean(touched.firstName && errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="lastName"
                      label="Apellido"
                      value={values.lastName}
                      error={Boolean(touched.lastName && errors.lastName)}
                      helperText={touched.lastName && errors.lastName}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="email"
                      label="Correo electrónico"
                      value={values.email}
                      variant="outlined"
                      fullWidth
                      disabled
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              <Box mt={3} />

              <SectionCard>
                <SectionHeader>
                  <SectionEyebrow>Perfil comercial</SectionEyebrow>
                  <SectionTitle>Datos requeridos</SectionTitle>
                  <SectionDescription>
                    Estos campos siguen siendo obligatorios para operar dentro
                    de la plataforma.
                  </SectionDescription>
                </SectionHeader>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="company"
                      label="Compañía"
                      value={values.company}
                      error={Boolean(touched.company && errors.company)}
                      helperText={touched.company && errors.company}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <StyledFormControl variant="outlined" required>
                      <InputLabel id="socialBusinessTypeLabel">
                        Tipo de negocio
                      </InputLabel>
                      <SelectWithAPI
                        id="businessType"
                        name="businessType"
                        labelid="socialBusinessTypeLabel"
                        label="Tipo de negocio"
                        value={values.businessType}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        url="/businessType"
                      />
                    </StyledFormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="phone"
                      label="Teléfono"
                      value={values.phone}
                      error={Boolean(touched.phone && errors.phone)}
                      helperText={touched.phone && errors.phone}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="mobileNumber"
                      label="Celular"
                      value={values.mobileNumber}
                      error={Boolean(
                        touched.mobileNumber && errors.mobileNumber
                      )}
                      helperText={touched.mobileNumber && errors.mobileNumber}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <StyledFormControl variant="outlined" required>
                      <InputLabel id="socialIdentificationTypeLabel">
                        Tipo identificación
                      </InputLabel>
                      <SelectWithAPI
                        id="identificationType"
                        name="identificationType"
                        labelid="socialIdentificationTypeLabel"
                        label="Tipo identificación"
                        value={values.identificationType}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        url="/identificationType"
                      />
                    </StyledFormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="identificationNumber"
                      label="Identificación"
                      value={values.identificationNumber}
                      error={Boolean(
                        touched.identificationNumber &&
                          errors.identificationNumber
                      )}
                      helperText={
                        touched.identificationNumber &&
                        errors.identificationNumber
                      }
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="streetAddress"
                      label="Dirección"
                      value={values.streetAddress}
                      error={Boolean(
                        touched.streetAddress && errors.streetAddress
                      )}
                      helperText={touched.streetAddress && errors.streetAddress}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="city"
                      label="Ciudad"
                      value={values.city}
                      error={Boolean(touched.city && errors.city)}
                      helperText={touched.city && errors.city}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <StyledFormControl variant="outlined" required>
                      <InputLabel id="socialCountryLabel">País</InputLabel>
                      <SelectWithAPI
                        id="country"
                        name="country"
                        labelid="socialCountryLabel"
                        label="País"
                        value={values.country}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        url="/country"
                      />
                    </StyledFormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="referral"
                      label={
                        pendingSocialRegistration.referral
                          ? "Código de invitación del partner"
                          : "Correo del partner (opcional)"
                      }
                      value={values.referral}
                      disabled={Boolean(pendingSocialRegistration.referral)}
                      helperText={
                        pendingSocialRegistration.referral
                          ? "La invitación fue aplicada automáticamente."
                          : undefined
                      }
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              <CheckboxRow>
                <Field
                  type="checkbox"
                  component={CheckboxWithLabel}
                  name="eula"
                  checked={values.eula}
                  Label={{
                    label: "He leído y acepto los términos y condiciones",
                  }}
                  value={values.eula}
                  key={values.eula}
                  error={Boolean(touched.eula && errors.eula)}
                  helperText={touched.eula && errors.eula}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </CheckboxRow>

              <SubmitButton
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={isSubmitting || !values.eula}
              >
                Activar cuenta con Google
              </SubmitButton>
            </form>
          )}
        </Formik>
      </Wrapper>
    </AuthLayout>
  );
}

export default SocialCompleteRegistration;
